import React from 'react'
import { Box, Button, CircularProgress } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { CompanyManagementPanelProps } from './types'
import { PlatformAdminView } from './components/platform/PlatformAdminView'
import { CompanyManagerView } from './components/manager/CompanyManagerView'
import { CompanyUserView } from './components/user/CompanyUserView'
import CompanyStep from '../Onboarding/steps/CompanyStep'
import { useCompanyManagement } from './hooks/useCompanyManagement'
import { useAppSelector } from '../../hooks'
import SignupClosedNotice from '../common/SignupClosedNotice'
import { usePermissionState } from '../../hooks/usePermission'
import { MANAGER_VIEW_KEYS } from './utils/managerViewKeys'

export const CompanyManagementPanel: React.FC<CompanyManagementPanelProps> = ({
  onBack,
  isPlatformAdmin = false,
  isCompanyManagerOrAdmin = false,
  isGlobalAdmin = false,
  currentCompany: propCurrentCompany,
  initialCompanyTab,
}) => {
  const { loading } = useCompanyManagement(
    isPlatformAdmin,
    isCompanyManagerOrAdmin,
    isGlobalAdmin,
    propCurrentCompany || null
  )
  const registrationClosed = useAppSelector(
    (state) => state.workspace.settings?.registration_enabled === false
  )
  // The management view opens for anyone who holds a permission it uses (SC-439); a member with
  // none gets the read-only company view. `isCompanyManagerOrAdmin` (the old role flag) no longer
  // decides it. While the answer is on its way the spinner shows, so a member never sees the
  // management view flash and an admin never lands on the member view first.
  const managerView = usePermissionState(MANAGER_VIEW_KEYS)

  // Show loading state while determining user role
  if (loading.loadingUserInfo || (propCurrentCompany && managerView.loading)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    )
  }

  // Platform Admin Interface - Can see all companies
  if (isPlatformAdmin || isGlobalAdmin) {
    return (
      <PlatformAdminView
        onBack={onBack}
        isPlatformAdmin={isPlatformAdmin}
        isGlobalAdmin={isGlobalAdmin}
        currentCompany={propCurrentCompany}
      />
    )
  }

  // Management Interface - anyone who holds a permission it uses
  if (managerView.allowed && propCurrentCompany && !isGlobalAdmin) {
    return (
      <CompanyManagerView
        onBack={onBack}
        isCompanyManagerOrAdmin={isCompanyManagerOrAdmin}
        currentCompany={propCurrentCompany}
        initialCompanyTab={initialCompanyTab}
      />
    )
  }

  // Regular User Interface - Can view their company (read-only)
  if (propCurrentCompany && !managerView.allowed && !isGlobalAdmin && !isPlatformAdmin) {
    return (
      <CompanyUserView
        onBack={onBack}
        currentCompany={propCurrentCompany}
      />
    )
  }

  // Every path below renders the Create Company form. While Registration Mode is Closed (the
  // go-to-market waitlist) cloud refuses self-serve creation, so show the notice instead. Any
  // /company/* page can get here when the company lookup is slow and the guard lets a user
  // without a company through. [SC-443]
  if (registrationClosed) {
    return <SignupClosedNotice onBack={onBack} />
  }

  // Special handling for setup tab - prevent flashing and ensure proper access control
  if (initialCompanyTab === 'setup') {
    // If user has company management access and a company exists, redirect to company management
    if (isCompanyManagerOrAdmin && propCurrentCompany) {
      // Redirect to company overview instead of showing setup
      if (onBack) {
        onBack()
      }
      return null
    }
    
    // Only show setup for users without company access
    if (!isCompanyManagerOrAdmin && !propCurrentCompany) {
      return (
        <CompanySetupPage onBack={onBack} />
      )
    }
    
    // If user has company access but no company (edge case), show setup
    if (!propCurrentCompany) {
      return (
        <CompanySetupPage onBack={onBack} />
      )
    }
    
    // Default fallback - redirect back
    if (onBack) {
      onBack()
    }
    return null
  }

  // Show company setup for users without company association
  return (
    <CompanySetupPage onBack={onBack} />
  )
}

export default CompanyManagementPanel 

/**
 * The company step of onboarding, reached from the menu by someone signed in without a company
 * (SC-438). One way to create a company, so it matches the wizard. The page reloads after
 * creation to pick up the new company's permissions.
 */
function CompanySetupPage({ onBack }: { onBack?: () => void }) {
  return (
    <Box sx={{ p: 3, maxWidth: 930, mx: 'auto' }}>
      {onBack && (
        <Button onClick={onBack} startIcon={<ArrowBackIcon />} variant="outlined" sx={{ mb: 3 }}>
          Back
        </Button>
      )}
      <CompanyStep onCreated={() => window.location.reload()} />
    </Box>
  )
}
